import type { EventBus } from './EventBus';

export const FICTION_START_MINUTES = 9 * 60;
export const FICTION_END_MINUTES = 17 * 60;
export const FICTION_MINUTES_SPAN = FICTION_END_MINUTES - FICTION_START_MINUTES;
export const RUNTIME_MS = 22 * 60 * 1000;

export interface ClockOptions {
  bus: EventBus;
  getNowMs: () => number;
}

export class Clock {
  private startRealMs: number | null = null;
  private pausedAtRealMs: number | null = null;
  private totalPausedMs = 0;
  private dayEndedEmitted = false;
  private lastFictionalMinutes = FICTION_START_MINUTES;

  constructor(private readonly opts: ClockOptions) {}

  start(): void {
    if (this.startRealMs !== null) return;
    this.startRealMs = this.opts.getNowMs();
  }

  pause(): void {
    if (this.pausedAtRealMs !== null) return;
    this.pausedAtRealMs = this.opts.getNowMs();
  }

  resume(): void {
    if (this.pausedAtRealMs === null) return;
    this.totalPausedMs += this.opts.getNowMs() - this.pausedAtRealMs;
    this.pausedAtRealMs = null;
  }

  tick(): void {
    if (this.startRealMs === null) return;
    const realElapsedMs = this.getEffectiveElapsedMs();
    const clampedElapsed = Math.min(realElapsedMs, RUNTIME_MS);
    const fictionalMinutes =
      FICTION_START_MINUTES + (clampedElapsed / RUNTIME_MS) * FICTION_MINUTES_SPAN;
    this.lastFictionalMinutes = fictionalMinutes;
    this.opts.bus.emit('clock:tick', { fictionalMinutes, realElapsedMs: clampedElapsed });
    if (!this.dayEndedEmitted && realElapsedMs >= RUNTIME_MS) {
      this.dayEndedEmitted = true;
      this.opts.bus.emit('day:ended', { realElapsedMs: RUNTIME_MS, unreadCount: 0 });
    }
  }

  get fictionalMinutes(): number {
    return this.lastFictionalMinutes;
  }

  private getEffectiveElapsedMs(): number {
    if (this.startRealMs === null) return 0;
    const now = this.opts.getNowMs();
    const paused =
      this.pausedAtRealMs !== null
        ? this.totalPausedMs + (now - this.pausedAtRealMs)
        : this.totalPausedMs;
    return now - this.startRealMs - paused;
  }
}

export function formatFictionalTime(fictionalMinutes: number): string {
  const total = Math.floor(fictionalMinutes);
  const hour24 = Math.floor(total / 60);
  const mins = total % 60;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const minStr = mins.toString().padStart(2, '0');
  return `${hour12}:${minStr} ${period}`;
}
